import { Handle, Position, useReactFlow, type Edge } from '@xyflow/react';
import { BaseNode } from '@/components/base-node';
import { useTranslation } from 'react-i18next';

type Option = { id: string; label: string; value: string };
type MenuNodeData = { options: Option[]; introMessage?: string };

const MenuNode = ({ id, data }: { id: string; data: MenuNodeData }) => {
  const { t } = useTranslation();
  const { setNodes, setEdges } = useReactFlow();
  const options: Option[] = data.options || [];
  const introMessage = data.introMessage || '';

  const addOption = () => {
    const newOption = { id: `opt-${options.length}`, label: '', value: `${options.length + 1}` };
    const newOptions = [...options, newOption];
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, options: newOptions } } : node
      )
    );
  };

  const onIntroMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, introMessage: e.target.value } } : node
      )
    );
  };

  const onChange = (optionId: string, field: 'value' | 'label', value: string) => {
    const newOptions = options.map((opt: Option) =>
      opt.id === optionId ? { ...opt, [field]: value } : opt
    );
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, options: newOptions } } : node
      )
    );
  };

  const removeOption = (optionId: string) => {
    const newOptions = options.filter((opt: Option) => opt.id !== optionId);
    // remove edges using this option as sourceHandle
  setEdges((eds: Edge[]) => eds.filter((e) => !(e.source === id && e.sourceHandle === optionId)));
    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, options: newOptions } } : node)),
    );
  };

  const removeNode = () => {
    // Remove edges connected to this node and the node itself
  setEdges((eds: Edge[]) => eds.filter((e) => e.source !== id && e.target !== id));
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
  };

  return (
    <BaseNode className="react-flow__node-menu">
      <Handle type="target" position={Position.Top} />
      <div className="p-4">
        <p className="font-bold">{t('optionsMenu')}</p>
        <textarea
          value={introMessage}
          onChange={onIntroMessageChange}
          className="w-full rounded-md border border-gray-300 p-2 mt-2"
          placeholder={t('menuIntroPlaceholder')}
          rows={3}
        />
        <div className="mt-2 space-y-2">
            {options.map((opt: Option) => (
              <div key={opt.id} className="flex items-center gap-2 relative">
                <input
                  className="border p-1 w-12"
                  placeholder={t('valuePlaceholder')}
                  value={opt.value}
                  onChange={(e) => onChange(opt.id, 'value', e.target.value)}
                />
                <input
                  className="border p-1 w-full"
                  placeholder={t('textToDisplayPlaceholder')}
                  value={opt.label}
                  onChange={(e) => onChange(opt.id, 'label', e.target.value)}
                />
                <button
                  title={t('removeOption')}
                  onClick={() => removeOption(opt.id)}
                  className="text-red-800 text-sm w-6 h-6 flex items-center justify-center border border-red-400 rounded mr-2"
                >
                  ×
                </button>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={opt.id}
                  style={{ background: 'blue' }}
                />
              </div>
            ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button onClick={addOption} className="bg-blue-500 text-white px-3 py-1 rounded">
            {t('addOption')}
          </button>
          <button onClick={removeNode} className="text-red-600 px-2 py-1 text-sm border border-red-300 rounded">
            {t('deleteNode')}
          </button>
        </div>
      </div>
    </BaseNode>
  );
};

export default MenuNode;
